import html2pdf from 'html2pdf.js';
import { DAYS, PERIODS, DOMAIN_COLORS } from '../lib/stores/soloTimetableStore';

/**
 * Export Solo timetable to PDF
 * @param {Object} grid - The timetable grid data
 * @param {string} timetableName - Name of the timetable
 * @param {Object} settings - Timetable settings
 */
export async function exportSoloTimetableToPDF(grid, timetableName, settings = {}) {
  if (!grid) {
    throw new Error('Timetable grid data is required');
  }

  const htmlContent = generateTimetableHTML(grid, timetableName, settings);

  const safeName = (timetableName || 'timetable')
    .replace(/[^a-z0-9]/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const options = {
    margin: 8,
    filename: `${safeName}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
  };

  await html2pdf().set(options).from(htmlContent).save();
}

/**
 * Get print-friendly domain colors
 */
function getPrintDomainColor(domain) {
  const colors = {
    'Communication': { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' },
    'SEMH': { bg: '#D1FAE5', border: '#10B981', text: '#065F46' },
    'Cognition': { bg: '#EDE9FE', border: '#8B5CF6', text: '#5B21B6' },
    'Sensory/Physical': { bg: '#FFEDD5', border: '#F97316', text: '#C2410C' },
    'Sensory': { bg: '#FFEDD5', border: '#F97316', text: '#C2410C' }
  };
  return colors[domain] || { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151' };
}

/**
 * Generate printable HTML for the timetable
 */
function generateTimetableHTML(grid, timetableName, settings) {
  const includeTransitions = settings?.includeTransitions !== false;
  const showTimes = settings?.showTimes !== false;

  const visiblePeriods = includeTransitions 
    ? PERIODS 
    : PERIODS.filter(p => p.type !== 'transition');

  const dateStr = new Date().toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  // Build table rows
  let tableRows = '';
  
  visiblePeriods.forEach(period => {
    const isTransition = period.type === 'transition';
    const isBreak = period.type === 'break';
    const rowHeight = isTransition ? '25px' : isBreak ? '40px' : '70px';
    const bgColor = isTransition ? '#FFFBEB' : isBreak ? (period.key === 'lunch' ? '#FEF3C7' : '#FEF9C3') : '#FFFFFF';
    
    tableRows += `<tr style="height: ${rowHeight};">`;
    
    // Time column
    tableRows += `
      <td style="
        border: 1px solid #D1D5DB; 
        padding: 6px 8px; 
        background-color: #F9FAFB;
        vertical-align: middle;
        width: 90px;
      ">
        <div style="font-weight: 600; font-size: 10px; color: #374151;">${period.label}</div>
        ${showTimes ? `<div style="font-size: 9px; color: #6B7280;">${period.time}</div>` : ''}
      </td>
    `;
    
    // Day columns
    DAYS.forEach(day => {
      const slot = grid[day]?.[period.key];
      const activity = slot?.activity;
      
      if (isBreak) {
        tableRows += `
          <td style="
            border: 1px solid #D1D5DB; 
            padding: 8px; 
            text-align: center;
            background-color: ${bgColor};
            vertical-align: middle;
          ">
            <div style="display: flex; align-items: center; justify-content: center; gap: 6px;">
              <span style="font-size: 16px;">${period.key === 'lunch' ? '🍽️' : '☕'}</span>
              <span style="font-weight: 500; color: #92400E; font-size: 11px;">${period.label}</span>
            </div>
          </td>
        `;
      } else if (isTransition) {
        tableRows += `
          <td style="
            border: 1px solid #D1D5DB; 
            padding: 4px; 
            text-align: center;
            background-color: ${bgColor};
            vertical-align: middle;
          ">
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              <span style="font-size: 12px;">🎵</span>
              <span style="font-size: 9px; color: #B45309;">${activity?.title || 'Transition'}</span>
            </div>
          </td>
        `;
      } else if (!activity) {
        tableRows += `
          <td style="
            border: 1px solid #D1D5DB; 
            padding: 6px; 
            text-align: center;
            color: #9CA3AF;
            background-color: ${bgColor};
            vertical-align: middle;
          ">
            <span style="font-size: 11px;">-</span>
          </td>
        `;
      } else {
        const domain = activity.targetDomains?.[0];
        const colors = getPrintDomainColor(domain);
        
        tableRows += `
          <td style="
            border: 1px solid #D1D5DB; 
            padding: 4px;
            background-color: ${bgColor};
            vertical-align: top;
          ">
            <div style="
              background-color: ${colors.bg};
              border-left: 4px solid ${colors.border};
              padding: 8px;
              border-radius: 4px;
              height: 100%;
              box-sizing: border-box;
            ">
              <div style="font-weight: 700; color: ${colors.text}; font-size: 11px; line-height: 1.3; margin-bottom: 4px;">
                ${activity.title}
              </div>
              ${activity.targetDomains && activity.targetDomains.length > 0 ? `
                <div style="display: flex; flex-wrap: wrap; gap: 3px;">
                  ${activity.targetDomains.slice(0, 2).map(d => {
                    const dColor = getPrintDomainColor(d);
                    return `<span style="
                      display: inline-block;
                      background-color: ${dColor.border}20;
                      color: ${dColor.text};
                      padding: 2px 6px;
                      border-radius: 10px;
                      font-size: 8px;
                      border: 1px solid ${dColor.border}40;
                    ">${d}</span>`;
                  }).join('')}
                </div>
              ` : ''}
            </div>
          </td>
        `;
      }
    });
    
    tableRows += '</tr>';
  });

  return `
    <div style="font-family: Arial, Helvetica, sans-serif; padding: 15px; background-color: #FFFFFF; color: #1F2937;">
      <!-- Header -->
      <div style="border-top: 6px solid #6366F1; padding-top: 15px; margin-bottom: 15px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="vertical-align: middle; width: 60px;">
              <img src="${window.location.origin}/brand/Logo%20cropped.png" alt="SEN-Ed" style="height: 45px; width: auto;" crossorigin="anonymous" />
            </td>
            <td style="vertical-align: middle; padding-left: 10px;">
              <h1 style="font-size: 24px; color: #1F2937; margin: 0; font-weight: 800;">${timetableName || 'Weekly Timetable'}</h1>
              <p style="font-size: 11px; color: #6B7280; margin: 4px 0 0 0;">Generated: ${dateStr}</p>
            </td>
            <td style="text-align: right; vertical-align: middle;">
              <div style="
                background: linear-gradient(135deg, #6366F1, #8B5CF6);
                color: white;
                padding: 8px 16px;
                border-radius: 20px;
                font-size: 11px;
                font-weight: 600;
                display: inline-block;
              ">
                SEN-Ed Weekly Schedule
              </div>
            </td>
          </tr>
        </table>
      </div>

      <!-- Student Name Field -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; background-color: #F8FAFC; border: 2px solid #E2E8F0; border-radius: 8px;">
        <tr>
          <td style="padding: 12px 15px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="width: 110px; vertical-align: bottom;">
                  <span style="font-size: 14px; font-weight: 700; color: #334155;">Student Name:</span>
                </td>
                <td style="vertical-align: bottom; padding-left: 8px;">
                  <div style="border-bottom: 2px solid #94A3B8; height: 24px; min-width: 250px;"></div>
                </td>
                <td style="width: 80px; vertical-align: bottom; padding-left: 20px;">
                  <span style="font-size: 14px; font-weight: 700; color: #334155;">Week of:</span>
                </td>
                <td style="vertical-align: bottom; padding-left: 8px;">
                  <div style="border-bottom: 2px solid #94A3B8; height: 24px; width: 150px;"></div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>

      <!-- Timetable -->
      <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
        <thead>
          <tr style="background: linear-gradient(135deg, #F3F4F6, #E5E7EB);">
            <th style="border: 1px solid #D1D5DB; padding: 10px 8px; font-weight: 700; text-align: left; width: 90px;">
              Time
            </th>
            ${DAYS.map(day => `
              <th style="border: 1px solid #D1D5DB; padding: 10px 8px; font-weight: 700; text-align: center;">
                ${day}
              </th>
            `).join('')}
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>

      <!-- Legend -->
      <div style="margin-top: 12px; display: flex; justify-content: center; gap: 20px; flex-wrap: wrap;">
        ${Object.entries({
          'Communication': '#3B82F6',
          'SEMH': '#10B981',
          'Cognition': '#8B5CF6',
          'Sensory/Physical': '#F97316'
        }).map(([domain, color]) => `
          <div style="display: flex; align-items: center; gap: 5px;">
            <div style="width: 14px; height: 14px; background-color: ${color}; border-radius: 3px;"></div>
            <span style="font-size: 10px; color: #6B7280;">${domain}</span>
          </div>
        `).join('')}
      </div>

      <!-- Footer -->
      <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #E5E7EB; text-align: center;">
        <p style="font-size: 9px; color: #9CA3AF; margin: 0;">
          Generated by SEN-Ed | www.sen-ed.co.uk | Print this timetable and display it where your learner can see it
        </p>
      </div>
    </div>
  `;
}

export default exportSoloTimetableToPDF;
